import styled from '@emotion/styled';
import { Button } from 'primereact/button';
import { createContext, ReactNode, useCallback, useContext, useState } from 'react';

import { Dialog } from '@/components/shared/components/Dialog';

interface PermissionDeniedDialogContextValue {
    show: (message: string) => void;
}

const PermissionDeniedDialogContext = createContext<PermissionDeniedDialogContextValue>({
    show: () => {},
});

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 8px 0;
`;

const Message = styled.p`
    margin: 0;
    color: #6b7280;
    font-size: 13px;
`;

const Actions = styled.div`
    display: flex;
    justify-content: flex-end;
`;

export const PermissionDeniedDialogProvider = ({ children }: { children: ReactNode }) => {
    const [message, setMessage] = useState<string | null>(null);

    const show = useCallback((nextMessage: string) => setMessage(nextMessage), []);
    const hide = useCallback(() => setMessage(null), []);

    return (
        <PermissionDeniedDialogContext.Provider value={{ show }}>
            {children}
            <Dialog
                id="permission-denied-modal"
                header="Permission Required"
                visible={message !== null}
                onHide={hide}
                data-testid="permission-denied-dialog"
            >
                <Container>
                    <Message data-testid="permission-denied-message">{message}</Message>
                    <Actions>
                        <Button
                            label="OK"
                            severity="info"
                            onClick={hide}
                            data-testid="permission-denied-confirm"
                        />
                    </Actions>
                </Container>
            </Dialog>
        </PermissionDeniedDialogContext.Provider>
    );
};

export const usePermissionDeniedDialog = () => useContext(PermissionDeniedDialogContext);
